import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Header } from '@/components/Header';
import { EventCard } from '@/components/EventCard';
import { FilterBar } from '@/components/FilterBar';
import { Button } from '@/components/ui/button';
import { Calendar, Search, Sparkles, X } from 'lucide-react';
import { Input } from '@/components/ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function HomePage() {
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    age_group: '',
    event_type: '',
    is_free: ''
  });

  useEffect(() => {
    fetchEvents();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [events, filters, searchQuery]);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/events`);
      if (!response.ok) throw new Error('Erreur lors du chargement');
      const data = await response.json();
      setEvents(data);
      setFilteredEvents(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let result = [...events];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(event => 
        event.name.toLowerCase().includes(query) ||
        event.description?.toLowerCase().includes(query) ||
        event.short_description?.toLowerCase().includes(query)
      );
    }

    // Age group filter
    if (filters.age_group) {
      result = result.filter(event => event.age_group === filters.age_group);
    }

    // Event type filter
    if (filters.event_type) {
      result = result.filter(event => event.event_type === filters.event_type);
    }

    // Price filter
    if (filters.is_free !== '') {
      const isFree = filters.is_free === 'true';
      result = result.filter(event => event.is_free === isFree);
    }

    setFilteredEvents(result);
  };

  const clearFilters = () => {
    setFilters({ age_group: '', event_type: '', is_free: '' });
    setSearchQuery('');
  };

  const hasActiveFilters = filters.age_group || filters.event_type || filters.is_free || searchQuery;

  return (
    <div className="min-h-screen bg-[#FFFDF7]">
      <Header />
      
      {/* Hero Section */}
      <section className="hero-gradient py-12 md:py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-2xl">
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-extrabold text-[#2D2A26] tracking-tight mb-4">
              Nos <span className="text-[#db3b17]">événements</span>
            </h1>
            <p className="text-lg md:text-xl text-[#645E55] leading-relaxed mb-8">
              Soirées jeux, ateliers, clubs lecture, tournois TCG... 
              Rejoignez notre communauté et vivez des moments uniques !
            </p>
            
            {/* Search bar */}
            <div className="flex gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#645E55]" />
                <Input
                  type="text"
                  placeholder="Rechercher un événement..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 h-14 bg-white border-2 border-[#E5E0D8] rounded-2xl text-lg focus:border-[#48b4ff] focus:ring-4 focus:ring-[#48b4ff]/10"
                  data-testid="search-input"
                />
              </div>
              <Button
                onClick={() => setShowFilters(!showFilters)}
                variant="outline"
                className={`h-14 px-6 rounded-2xl border-2 ${showFilters ? 'bg-[#ffd451] border-[#ffd451]' : 'border-[#E5E0D8]'}`}
                data-testid="toggle-filters"
              >
                <Sparkles className="w-5 h-5 mr-2" />
                Filtres
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Filters Section */}
      {showFilters && (
        <section className="bg-white border-b border-[#E5E0D8] py-6 px-4 animate-slide-up">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-lg text-[#2D2A26]">Filtrer les événements</h3>
              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  onClick={clearFilters}
                  className="text-[#db3b17] hover:text-[#b92b0c]"
                  data-testid="clear-filters"
                >
                  <X className="w-4 h-4 mr-2" />
                  Effacer les filtres
                </Button>
              )}
            </div>
            <FilterBar filters={filters} onFilterChange={setFilters} />
          </div>
        </section>
      )}

      {/* Events Grid */}
      <section className="py-12 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Results count */}
          <div className="flex items-center justify-between mb-8">
            <p className="text-[#645E55]">
              <span className="font-bold text-[#2D2A26]">{filteredEvents.length}</span> événement{filteredEvents.length !== 1 ? 's' : ''} à venir
            </p>
          </div>

          {/* Loading state */}
          {loading && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-white rounded-2xl overflow-hidden border border-[#E5E0D8]">
                  <div className="h-48 skeleton" />
                  <div className="p-5 space-y-3">
                    <div className="h-4 w-32 skeleton rounded" />
                    <div className="h-6 w-3/4 skeleton rounded" />
                    <div className="h-4 w-1/2 skeleton rounded" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Error state */}
          {error && !loading && (
            <div className="text-center py-16">
              <div className="text-6xl mb-4">😕</div>
              <h3 className="font-heading font-bold text-xl text-[#2D2A26] mb-2">Oups !</h3>
              <p className="text-[#645E55] mb-6">{error}</p>
              <Button onClick={fetchEvents} className="btn-primary">
                Réessayer
              </Button>
            </div>
          )}

          {/* Empty state */}
          {!loading && !error && filteredEvents.length === 0 && (
            <div className="text-center py-16">
              <div className="text-6xl mb-4">🎲</div>
              <h3 className="font-heading font-bold text-xl text-[#2D2A26] mb-2">
                {hasActiveFilters ? 'Aucun événement trouvé' : 'Pas d\'événement prévu'}
              </h3>
              <p className="text-[#645E55] mb-6">
                {hasActiveFilters 
                  ? 'Essayez de modifier vos critères de recherche'
                  : 'Revenez bientôt pour découvrir nos prochains événements !'}
              </p>
              {hasActiveFilters && (
                <Button onClick={clearFilters} className="btn-secondary">
                  Effacer les filtres
                </Button>
              )}
            </div>
          )}

          {/* Events grid */}
          {!loading && !error && filteredEvents.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredEvents.map((event, index) => (
                <div 
                  key={event.event_id}
                  className="animate-slide-up"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <EventCard event={event} featured={index === 0} />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#2D2A26] text-white py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img 
                src="https://media.ulule.com/presales/3/8/9/8/7/1/178983/logo_v1--2-.5BO4CeI5zf.jpg" 
                alt="Plume et Pion"
                className="h-12 w-auto rounded-xl object-contain"
              />
              <div>
                <h3 className="font-heading font-bold">Plume et Pion</h3>
                <p className="text-white/60 text-sm">Votre librairie ludique près de Tours</p>
              </div>
            </div>
            <p className="text-white/60 text-sm">
              © {new Date().getFullYear()} Plume et Pion. Tous droits réservés.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
